import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router";
import { ArrowRight, FileText, Menu, PanelBottom } from "lucide-react";
import { Button, EmptyState, Pagination } from "../../components/ui";
import {
  PagesListToolbar,
  type ToolbarFilters,
  type StatusTab,
} from "./pages-list/PagesListToolbar";
import { PageRow } from "./pages-list/PageRow";
import { ALL_PAGES } from "./pages/queries";
import { GET_NAV_CONFIG } from "./navigation/queries";
import { GET_FOOTER_CONFIG } from "./footer/queries";
import { useBrandingQuery } from "../../apollo/branding";
import { NavPreview } from "./navigation/NavPreview";
import { FooterPreview, type FooterLayout, type FooterOptions } from "./footer/FooterPreview";
import type { PageForListing } from "./pages/types";

const PER_PAGE = 15;

interface QueryData {
  allPages: PageForListing[];
  _allPagesMeta: { count: number };
}

const INITIAL_FILTERS: ToolbarFilters = {
  search: "",
  status: "all",
};

export function PagesListing() {
  const [filters, setFilters] = useState<ToolbarFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.status]);

  const filterVar = useMemo(
    () => buildFilter(filters),
    [filters.search, filters.status],
  );

  const { data, previousData, loading, error, refetch } =
    useQuery<QueryData>(ALL_PAGES, {
      variables: { page, perPage: PER_PAGE, filter: filterVar },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    });

  const stable = data ?? previousData;
  const pages = stable?.allPages ?? [];
  const total = stable?._allPagesMeta?.count ?? 0;
  const initialLoad = loading && !stable;

  const filtersActive =
    filters.search.trim().length > 0 || filters.status !== "all";

  if (error && !stable) {
    return (
      <div className="grid grid-cols-12 gap-3">
        <section className="shadow-card col-span-12 rounded-lg bg-paper-hi p-8 text-center">
          <div className="mb-1 text-[14px] font-medium">
            Couldn't load pages
          </div>
          <div className="mb-4 text-[12.5px] text-ink-4">{error.message}</div>
          <Button variant="primary" onClick={() => refetch()}>
            Try again
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="shadow-card col-span-12 lg:col-span-8 overflow-hidden rounded-lg bg-paper-hi">
        <div className="hair-b border-b flex items-center justify-between gap-3 px-5 py-4">
          <div>
            <h2 className="text-[15px] font-semibold">Pages</h2>
            <p className="mt-0.5 text-[12px] text-ink-4">
              {filtersActive
                ? `${total} matching ${total === 1 ? "page" : "pages"}`
                : `${total} total ${total === 1 ? "page" : "pages"}`}
            </p>
          </div>
          <PagesListToolbar filters={filters} onFiltersChange={setFilters} />
        </div>

        {initialLoad ? (
          <RowSkeletons />
        ) : pages.length === 0 ? (
          filtersActive ? (
            <EmptyState
              icon={<FileText size={28} strokeWidth={1.5} />}
              heading="No matching pages"
              description="Try a different search term or clear the filter."
            />
          ) : (
            <EmptyState
              icon={<FileText size={28} strokeWidth={1.5} />}
              heading="No pages yet"
              description="Create a page to start building your website — home, about, services, and more."
            />
          )
        ) : (
          <div
            className={`divide-hair ${loading ? "opacity-60" : ""} transition-opacity`}
          >
            {pages.map((p) => (
              <PageRow key={p.id} page={p} />
            ))}
          </div>
        )}

        {!initialLoad && total > 0 && (
          <div className="hair-t border-t bg-canvas/40 px-5 py-3">
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onPageChange={setPage}
              unit="page"
            />
          </div>
        )}
      </section>

      {/* Preview sidebar */}
      <div className="col-span-12 lg:col-span-4 space-y-3">
        <NavPreviewCard />
        <FooterPreviewCard />
      </div>
    </div>
  );
}

function buildFilter(filters: ToolbarFilters):
  | {
      status?: string;
      q?: string;
    }
  | undefined {
  const search = filters.search.trim();
  const tabStatus = mapTabToStatus(filters.status);
  if (!search && !tabStatus) return undefined;
  return {
    ...(tabStatus ? { status: tabStatus } : {}),
    ...(search ? { q: search } : {}),
  };
}

function mapTabToStatus(tab: StatusTab): string | null {
  switch (tab) {
    case "draft":
      return "draft";
    case "published":
      return "published";
    default:
      return null;
  }
}

// ── Preview Cards ────────────────────────────────────────────

const DEFAULT_FOOTER_OPTIONS: FooterOptions = {
  showSocialLinks: true,
  showContactInfo: true,
  showQuickLinks: true,
  showLegalInfo: true,
  socialTitle: "Follow Us",
  contactTitle: "Contact",
  quickLinksTitle: "Quick Links",
  legalTitle: "Legal",
};

const DEFAULT_SECTION_ORDER = ["social", "quickLinks", "contact", "legal"];

/**
 * The listing sidebar previews the chrome the CMS site actually renders:
 * the CMS chrome zone (draft first, then published) with fallback to the
 * legacy column. Zones are stored in the legacy shapes, so the existing
 * parsers apply to both.
 */
function resolveChromeZone(
  tenant:
    | {
        chromeConfigDraft?: string | null;
        chromeConfigPublished?: string | null;
      }
    | null
    | undefined,
  zone: "nav" | "footer",
  legacy: string | null | undefined,
): string | null {
  for (const raw of [tenant?.chromeConfigDraft, tenant?.chromeConfigPublished]) {
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed[zone]) {
        return JSON.stringify(parsed[zone]);
      }
    } catch {
      // malformed — try the next source
    }
  }
  return legacy ?? null;
}

function parseNavConfig(raw: string | null | undefined) {
  if (!raw) return { headerBehavior: "fixed" as const, items: [] as { key: string; label: string; primary: boolean }[] };
  try {
    const parsed = JSON.parse(raw);
    return {
      headerBehavior: (parsed.headerBehavior ?? "fixed") as "static" | "fixed" | "sticky",
      items: (parsed.items ?? []).map((it: Record<string, unknown>, i: number) => ({
        key: (it.key as string) ?? `item-${i}`,
        label: (it.label as string) ?? "",
        primary: Boolean(it.primary),
      })),
    };
  } catch {
    return { headerBehavior: "fixed" as const, items: [] as { key: string; label: string; primary: boolean }[] };
  }
}

function parseFooterConfig(raw: string | null | undefined) {
  const defaults = {
    layout: "columns-4" as FooterLayout,
    options: { ...DEFAULT_FOOTER_OPTIONS },
    quickLinks: [] as { source: "feature" | "page" | "custom"; sourceId: string; label: string; url?: string; key: string }[],
    sectionOrder: [...DEFAULT_SECTION_ORDER],
  };
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw);
    const flat = parsed.options ? { ...parsed, ...parsed.options } : parsed;
    return {
      layout: (flat.layout ?? "columns-4") as FooterLayout,
      options: {
        showSocialLinks: flat.showSocialLinks ?? DEFAULT_FOOTER_OPTIONS.showSocialLinks,
        showContactInfo: flat.showContactInfo ?? DEFAULT_FOOTER_OPTIONS.showContactInfo,
        showQuickLinks: flat.showQuickLinks ?? DEFAULT_FOOTER_OPTIONS.showQuickLinks,
        showLegalInfo: flat.showLegalInfo ?? DEFAULT_FOOTER_OPTIONS.showLegalInfo,
        socialTitle: flat.socialTitle ?? DEFAULT_FOOTER_OPTIONS.socialTitle,
        contactTitle: flat.contactTitle ?? DEFAULT_FOOTER_OPTIONS.contactTitle,
        quickLinksTitle: flat.quickLinksTitle ?? DEFAULT_FOOTER_OPTIONS.quickLinksTitle,
        legalTitle: flat.legalTitle ?? DEFAULT_FOOTER_OPTIONS.legalTitle,
      },
      quickLinks: Array.isArray(flat.quickLinks)
        ? flat.quickLinks.map((l: { source?: string; sourceId?: string; label?: string; url?: string; key?: string }) => ({
            source: (l.source ?? "custom") as "feature" | "page" | "custom",
            sourceId: l.sourceId ?? "",
            label: l.label ?? "",
            url: l.url,
            key: l.key || crypto.randomUUID(),
          }))
        : [],
      sectionOrder: Array.isArray(flat.sectionOrder) ? flat.sectionOrder as string[] : [...DEFAULT_SECTION_ORDER],
    };
  } catch {
    return defaults;
  }
}

function parseSocialLinks(raw: string | null | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
    return {};
  } catch {
    return {};
  }
}

function parseAddresses(raw: string | null | undefined): { key?: string; label?: string; street?: string; city?: string; state?: string; zip?: string }[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function NavPreviewCard() {
  const { data } = useQuery(GET_NAV_CONFIG);
  const { data: brandingData } = useBrandingQuery();
  const tenant = data?.currentTenant;

  const config = useMemo(
    () => parseNavConfig(resolveChromeZone(tenant, "nav", tenant?.navConfig)),
    [tenant],
  );

  const branding = brandingData?.branding;
  const primaryColor = branding?.primaryColor ?? "#2663C7";
  const logoUrl = tenant?.logoUrl ?? branding?.logoUrl ?? null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-1">
        <Menu size={14} strokeWidth={1.8} className="text-ink-4" />
        <h3 className="text-[13px] font-semibold text-ink">Navigation</h3>
      </div>
      <NavPreview
        items={config.items}
        headerBehavior={config.headerBehavior}
        logoUrl={logoUrl}
        primaryColor={primaryColor}
        tenantName={tenant?.name ?? ""}
        domain={tenant?.domain ?? ""}
      />
      <div className="px-1">
        <Link
          to="/website/chrome"
          className="flex items-center gap-1 text-[12.5px] font-medium text-tide hover:underline"
        >
          Edit in Header &amp; Footer
          <ArrowRight size={12} strokeWidth={2} />
        </Link>
      </div>
    </div>
  );
}

function FooterPreviewCard() {
  const { data } = useQuery(GET_FOOTER_CONFIG);
  const { data: brandingData } = useBrandingQuery();
  const tenant = data?.currentTenant;

  const footerConfig = useMemo(
    () =>
      parseFooterConfig(
        resolveChromeZone(tenant, "footer", tenant?.footerConfig),
      ),
    [tenant],
  );

  const socialLinks = useMemo(
    () => parseSocialLinks(tenant?.socialLinks),
    [tenant?.socialLinks],
  );

  const addresses = useMemo(
    () => parseAddresses(tenant?.addresses),
    [tenant?.addresses],
  );

  const branding = brandingData?.branding;
  const primaryColor = branding?.primaryColor ?? "#2663C7";
  const bgColor = branding?.backgroundColor ?? primaryColor;
  const logoUrl = tenant?.logoUrl ?? branding?.logoUrl ?? null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-1">
        <PanelBottom size={14} strokeWidth={1.8} className="text-ink-4" />
        <h3 className="text-[13px] font-semibold text-ink">Footer</h3>
      </div>
      <FooterPreview
        layout={footerConfig.layout}
        options={footerConfig.options}
        logoUrl={logoUrl}
        primaryColor={primaryColor}
        bgColor={bgColor}
        tenantName={tenant?.name ?? ""}
        domain={tenant?.domain ?? ""}
        phone={tenant?.phone ?? null}
        email={tenant?.email ?? null}
        socialLinks={socialLinks}
        addresses={addresses}
        legalEntity={tenant?.legalEntityName ?? null}
        legalDisclaimers={tenant?.legalDisclaimers ?? null}
        quickLinks={footerConfig.quickLinks}
        availablePages={[]}
        availableFeatures={[]}
        sectionOrder={footerConfig.sectionOrder}
      />
      <div className="px-1">
        <Link
          to="/website/chrome"
          className="flex items-center gap-1 text-[12.5px] font-medium text-tide hover:underline"
        >
          Edit in Header &amp; Footer
          <ArrowRight size={12} strokeWidth={2} />
        </Link>
      </div>
    </div>
  );
}

function RowSkeletons() {
  return (
    <div className="divide-hair">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="pulse-soft h-3 w-1/4 rounded bg-canvas-2" />
            <div className="pulse-soft h-4 w-3/4 rounded bg-canvas-2" />
            <div className="pulse-soft h-3 w-1/2 rounded bg-canvas-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
